import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { AuthenticatedCustomer, CustomerProfile } from '@/components/Modal/LoginModal/utils';

/**
 * Customer authentication store for reactive auth state.
 *
 * Use this store when components need to re-render on auth changes (e.g., conditionally rendering login/logout buttons).
 * For one-time checks in event handlers or server components, use `isCustomerAuthenticated()` / `getAuthenticatedCustomer()` from utils.
 *
 * The store is synced with localStorage via:
 * - `CustomerAuthProvider` on app mount
 * - `saveLoginCredentailsAsCookie()` after login
 * - `customerSignOut()` on logout
 */
interface CustomerAuthState {
	isAuthenticated: boolean;
	user: CustomerProfile | undefined;
	token: string | undefined;
}

interface CustomerAuthActions {
	clearAuth: () => void;
	syncFromStorage: (authData: AuthenticatedCustomer | undefined) => void;
}

type CustomerAuthStore = CustomerAuthState & CustomerAuthActions;

const isNotProd = process.env.NODE_ENV !== 'production';

export const useCustomerAuthStore = create<CustomerAuthStore>()(
	devtools(
		(set) => ({
			isAuthenticated: false,
			user: undefined,
			token: undefined,

			clearAuth: () => set({ isAuthenticated: false, user: undefined, token: undefined }, false, 'clearAuth'),

			syncFromStorage: (authData) => {
				if (authData) {
					set({ isAuthenticated: true, user: authData.user, token: authData.token }, false, 'syncFromStorage');
				} else {
					set({ isAuthenticated: false, user: undefined, token: undefined }, false, 'syncFromStorage');
				}
			}
		}),
		{ enabled: isNotProd, name: 'customer-auth' }
	)
);
